import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { leaveRequestService, LeaveRequest, LeaveRequestCreateEntry } from '../services/leaveRequestService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, Plus, Calendar, CheckCircle2, XCircle, Clock, Trash2 } from 'lucide-react';
import { format, parseISO, isAfter, isBefore, startOfDay } from 'date-fns';

const ModernLeaveRequests: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();
  
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState<Partial<LeaveRequestCreateEntry>>({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    leaveType: 'unpaid',
    reason: '',
  });

  const isManager = user?.roles?.some(role => ['admin', 'orgAdmin', 'storeManager'].includes(role));

  useEffect(() => {
    fetchRequests();
  }, [user]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      let data: LeaveRequest[] = [];
      if (isManager) {
        data = await leaveRequestService.getLeaveRequests();
      } else if (user?.id) {
        data = await leaveRequestService.getLeaveRequestsByStaff(user.id);
      }
      setRequests(data);
    } catch (error) {
      console.error('Failed to fetch leave requests:', error);
      notify(translate('pos.staff.leave.fetch_failed', 'Failed to load leave requests'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !formData.startDate || !formData.endDate) return;

    if (isBefore(parseISO(formData.endDate as string), parseISO(formData.startDate as string))) {
      notify(translate('pos.staff.leave.invalid_dates', 'End date cannot be before start date'), { type: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      await leaveRequestService.createLeaveRequest({
        staffId: user.id,
        staffName: `${user.firstName} ${user.lastName}`,
        startDate: formData.startDate,
        endDate: formData.endDate,
        leaveType: formData.leaveType as any,
        reason: formData.reason,
        status: 'pending',
      });
      notify(translate('pos.staff.leave.create_success', 'Leave request submitted successfully'), { type: 'success' });
      setIsDialogOpen(false);
      setFormData({
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
        leaveType: 'unpaid',
        reason: '',
      });
      fetchRequests();
    } catch (error) {
      console.error('Failed to create leave request:', error);
      notify(translate('pos.staff.leave.create_failed', 'Failed to submit leave request'), { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected', rejectionReason?: string) => {
    try {
      await leaveRequestService.updateLeaveRequest(id, { 
        status, 
        rejectionReason,
        approvedBy: user?.id,
        approvedAt: new Date().toISOString()
      });
      notify(translate(`pos.staff.leave.${status}_success`, `Leave request ${status} successfully`), { type: 'success' });
      fetchRequests();
    } catch (error) {
      console.error(`Failed to ${status} leave request:`, error);
      notify(translate(`pos.staff.leave.${status}_failed`, `Failed to ${status} leave request`), { type: 'error' });
    }
  };

  const handleDeleteRequest = async (id: string) => {
    if (!window.confirm(translate('pos.common.confirm_delete', 'Are you sure you want to delete this?'))) return;
    try {
      await leaveRequestService.deleteLeaveRequest(id);
      notify(translate('pos.staff.leave.delete_success', 'Leave request deleted successfully'), { type: 'success' });
      fetchRequests();
    } catch (error) {
      console.error('Failed to delete leave request:', error);
      notify(translate('pos.staff.leave.delete_failed', 'Failed to delete leave request'), { type: 'error' });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500 hover:bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> {translate('pos.staff.leave.status_approved', 'Approved')}</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> {translate('pos.staff.leave.status_rejected', 'Rejected')}</Badge>;
      case 'cancelled':
        return <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" /> {translate('pos.staff.leave.status_cancelled', 'Cancelled')}</Badge>;
      default:
        return <Badge variant="outline" className="text-orange-500 border-orange-500"><Clock className="w-3 h-3 mr-1" /> {translate('pos.staff.leave.status_pending', 'Pending')}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">{translate('pos.staff.leave.title', 'Leave Requests')}</h2>
          <p className="text-muted-foreground">{translate('pos.staff.leave.subtitle', 'Manage and request days off')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              {translate('pos.staff.leave.request_button', 'Request Day Off')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{translate('pos.staff.leave.new_request_title', 'Submit Leave Request')}</DialogTitle>
              <DialogDescription>
                {translate('pos.staff.leave.new_request_desc', 'Fill in the details for your day off request.')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRequest} className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">{translate('pos.common.start_date', 'Start Date')}</Label>
                  <Input 
                    id="startDate" 
                    type="date" 
                    value={formData.startDate} 
                    onChange={e => setFormData({...formData, startDate: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">{translate('pos.common.end_date', 'End Date')}</Label>
                  <Input 
                    id="endDate" 
                    type="date" 
                    value={formData.endDate} 
                    onChange={e => setFormData({...formData, endDate: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="leaveType">{translate('pos.staff.leave.type_label', 'Type')}</Label>
                <Select 
                  value={formData.leaveType} 
                  onValueChange={value => setFormData({...formData, leaveType: value as any})}
                >
                  <SelectTrigger id="leaveType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">{translate('pos.staff.leave.type_unpaid', 'Unpaid')}</SelectItem>
                    <SelectItem value="paid">{translate('pos.staff.leave.type_paid', 'Paid')}</SelectItem>
                    <SelectItem value="sick">{translate('pos.staff.leave.type_sick', 'Sick Leave')}</SelectItem>
                    <SelectItem value="other">{translate('pos.staff.leave.type_other', 'Other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reason">{translate('pos.common.reason', 'Reason')}</Label>
                <Textarea 
                  id="reason" 
                  placeholder={translate('pos.staff.leave.reason_placeholder', 'Why do you need this day off?')}
                  value={formData.reason}
                  onChange={e => setFormData({...formData, reason: e.target.value})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {translate('pos.common.cancel', 'Cancel')}
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {translate('pos.common.submit', 'Submit Request')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {isManager && <TableHead>{translate('pos.common.staff', 'Staff')}</TableHead>}
                <TableHead>{translate('pos.common.dates', 'Dates')}</TableHead>
                <TableHead>{translate('pos.staff.leave.type', 'Type')}</TableHead>
                <TableHead>{translate('pos.common.reason', 'Reason')}</TableHead>
                <TableHead>{translate('pos.common.status', 'Status')}</TableHead>
                <TableHead className="text-right">{translate('pos.common.actions', 'Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isManager ? 6 : 5} className="h-24 text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                  </TableCell>
                </TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isManager ? 6 : 5} className="h-24 text-center text-muted-foreground">
                    {translate('pos.staff.leave.no_requests', 'No leave requests found')}
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    {isManager && <TableCell className="font-medium">{request.staffName}</TableCell>}
                    <TableCell>
                      <div className="flex items-center text-xs">
                        <Calendar className="w-3 h-3 mr-1 text-muted-foreground" />
                        {format(parseISO(request.startDate), 'MMM dd, yyyy')} - {format(parseISO(request.endDate), 'MMM dd, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize text-xs">{request.leaveType}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs" title={request.reason}>
                      {request.reason || '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(request.status)}</TableCell>
                    <TableCell className="text-right">
                      {isManager && request.status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:text-green-800"
                            onClick={() => handleUpdateStatus(request.id, 'approved')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="bg-red-50 border-red-200 text-red-700 hover:bg-red-100 hover:text-red-800"
                            onClick={() => {
                              const reason = window.prompt(translate('pos.staff.leave.rejection_reason_prompt', 'Enter rejection reason:'));
                              if (reason !== null) handleUpdateStatus(request.id, 'rejected', reason);
                            }}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        (!isManager || request.staffId === user?.id) && request.status === 'pending' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteRequest(request.id)}
                            className="text-destructive hover:text-destructive hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernLeaveRequests;
